import { createReducer, on } from '@ngrx/store';

import { Organization } from '../../models/organization.class';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';
import { CompletedSwitchToOrganization, SessionLogoutSuccessfull, SetCurrentOrganization } from '../actions';
import * as LocationActionTypes from '../actions/locations.actions';
import { locationsInitialState } from '../states/locations.state';

export const LOCATIONS_REDUCER_KEY = 'locations';
export const LOCATIONS_REDUCER = createReducer(
    locationsInitialState,
    on(CompletedSwitchToOrganization, (state, action) => {
        const organizationId = action.organization?.id;
        return {
            ...state,
            currentOrganizationId: organizationId,
        };
    }),
    on(SetCurrentOrganization, (state, action) => {
        const organizationId = action.organization?.id;
        return {
            ...state,
            currentOrganizationId: organizationId,
        };
    }),
    on(LocationActionTypes.UpdatedLocation, (state, action) => {
        const organizations: Organization[] = CommonUtilsService.cloneObject(state?.organizationLocations?.[action.organizationId] || []);
        if (action.parentId && action.organization.id !== action.parentId) {
            const orgIndex = organizations?.findIndex(org => org.id === action.organization.id);
            if (orgIndex > -1) {
                organizations.splice(orgIndex, 1, action.organization);
            } else {
                organizations.unshift(action.organization);
            }
        }
        const organizationLocations = { ...state.organizationLocations };
        if (action.parentId) organizationLocations[action.parentId] = organizations;
        return {
            ...state,
            organizationLocations: organizationLocations,
        };
    }),
    on(LocationActionTypes.SetLocation, (state, action) => {
        const organizations: Organization[] = CommonUtilsService.cloneObject(state?.organizationLocations?.[action.organizationId] || []);
        if (action.organization.id !== action.parentId) {
            const orgIndex = organizations?.findIndex(org => org.id === action.organization.id);
            if (orgIndex > -1) {
                organizations.splice(orgIndex, 1, action.organization);
            } else {
                organizations.unshift(action.organization);
            }
        }
        const organizationLocations = { ...state.organizationLocations };
        if (action.parentId) organizationLocations[action.parentId] = organizations;
        return {
            ...state,
            organizationLocations: organizationLocations,
        };
    }),
    on(LocationActionTypes.SetLocationInAssociationsTree, (state, action) => {
        const orgTree: Organization[] = CommonUtilsService.cloneObject(state.associatedOrganizations) as Organization[];
        setInOrgTree(orgTree, action.organization);
        return {
            ...state,
            associatedOrganizations: orgTree,
            associatedOrgIdVsNameMap: {
                ...state.associatedOrgIdVsNameMap,
                [action.organizationId]: action.organization?.name,
            },
        };
    }),
    on(LocationActionTypes.UpdateLocationInAssociationsTree, (state, action) => {
        const orgTree: Organization[] = CommonUtilsService.cloneObject(state.associatedOrganizations);
        action.organizations?.forEach(organization => {
            const found = updateOrgTree(orgTree, organization);
            if (found || organization?.id !== action.organizationId) {
                return;
            }
            const completed = setInOrgTree(orgTree, organization);
            if (!completed) {
                orgTree.push(organization);
            }
        });
        return {
            ...state,
            associatedOrganizations: orgTree,
            associatedOrgIdVsNameMap: {
                ...state.associatedOrgIdVsNameMap,
                ...action.organizations?.reduce((map, organization) => {
                    map[organization.id] = organization.name;
                    return map;
                }, {}),
            },
        };
    }),
    on(LocationActionTypes.SetLocationsCodes, (state, action) => {
        return {
            ...state,
            locationCodes: action.locationCodes,
        };
    }),
    on(LocationActionTypes.SetFlexFields, (state, action) => {
        return {
            ...state,
            flexfields: action.flexFields,
        };
    }),
    on(LocationActionTypes.SetOrganizationLocations, (state, action) => {
        if (!action.organizationId) return state;
        return {
            ...state,
            organizationLocations: {
                ...state.organizationLocations,
                [action.organizationId]: action.organizations,
            },
        };
    }),
    on(LocationActionTypes.SetAssociatedLocations, (state, action) => {
        return {
            ...state,
            associatedOrganizations: action.organizations,
            associatedOrgIdVsNameMap:
                action.nonModifiedOrgs?.reduce((map, organization) => {
                    map[organization.id] = organization.name;
                    return map;
                }, {}) || state.associatedOrgIdVsNameMap,
        };
    }),
    on(LocationActionTypes.SetLocationLogo, (state, action) => {
        return {
            ...state,
            logoMapWithHierarchy: {
                ...state.logoMapWithHierarchy,
                [action.currentOrgId]: action.locationHierarchy ? action.logoData : state.logoMapWithHierarchy?.[action.currentOrgId],
            },
            logoMap: {
                ...state.logoMap,
                [action.currentOrgId]: !action.locationHierarchy ? action.logoData : state.logoMap?.[action.currentOrgId],
            },
        };
    }),
    on(LocationActionTypes.SetOrganizationNamesByIds, (state, action) => {
        return {
            ...state,
            orgIdsVsOrgNames: {
                ...state.orgIdsVsOrgNames,
                ...action.orgIdsVsOrgNames,
            },
        };
    }),
    on(LocationActionTypes.SetOrganizationChildHierarchy, (state, action) => {
        return {
            ...state,
            orgChildHierarchy: {
                ...state.organizationLocations,
                [action.orgId]: action.childHierarchy,
            },
        };
    }),
    on(LocationActionTypes.SetRelatedTenants, (state, action) => {
        return {
            ...state,
            relatedTenants: {
                ...state.relatedTenants,
                [action.serviceId]: action.relatedTenants,
            },
        };
    }),
    on(SessionLogoutSuccessfull, () => locationsInitialState)
);

const updateOrgTree = (tree: Organization[], organization: Organization) => {
    let found = false;
    tree?.forEach(org => {
        if (found) {
            return;
        }
        if (org.id === organization.id) {
            found = true;
            Object.assign(org, organization);
            return;
        }
        if (org.childNodes?.length > 0) {
            found = updateOrgTree(org.childNodes, organization);
        }
    });
    return found;
};

const setInOrgTree = (tree: Organization[], organization: Organization) => {
    let found = false;
    tree?.forEach(org => {
        if (found) {
            return;
        }
        if (org.id === organization.parentId) {
            found = true;
            org.nextNodes = org.nextNodes || [];
            org.childNodes = org.childNodes || [];
            org.nextNodes.push(organization.id);
            org.childNodes.push(organization);
            return;
        }
        if (org.childNodes?.length > 0) {
            found = setInOrgTree(org.childNodes, organization);
        }
    });
    return found;
};
